import React, { useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Stack,
  Group,
  TextInput,
  Select,
  Button,
  Text,
  Box,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconFilter,
} from "@tabler/icons-react";
import type { MenuItem, MenuPage } from "./types";
import { ItemRow } from "./ItemRow";
import { getItemCategories } from "./hooks/useMenuV2";

interface Props {
  page: MenuPage;
  allCategories: string[];
  onUpdateItem: (itemId: string, updates: Partial<MenuItem>) => Promise<void>;
  onDeleteItem: (itemId: string) => Promise<void>;
  onCreateItem: (data: Partial<MenuItem>) => Promise<void>;
}

export const ItemList: React.FC<Props> = ({
  page,
  allCategories,
  onUpdateItem,
  onDeleteItem,
  onCreateItem,
}) => {
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const isMobile = useMediaQuery("(max-width: 768px)");

  const items = useMemo(() => page.menuItems || [], [page.menuItems]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (q && !(it.name || "").toLowerCase().includes(q)) return false;
      if (filterCategory) {
        const cats = getItemCategories(it);
        if (!cats.includes(filterCategory)) return false;
      }
      return true;
    });
  }, [items, search, filterCategory]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    await onCreateItem({ name: newName.trim() });
    setNewName("");
    setCreating(false);
  };

  return (
    <Stack gap="md" p={isMobile ? "sm" : "lg"} style={{ height: "100%", overflow: "hidden" }}>
      {isMobile ? (
        <Stack gap="xs">
          <Group gap="xs" wrap="nowrap">
            <TextInput
              placeholder="Search items…"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={14} />}
              style={{ flex: 1 }}
            />
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => setCreating(true)}
              style={{ flexShrink: 0 }}
            >
              Add
            </Button>
          </Group>
          <Group gap="xs" wrap="nowrap">
            <Select
              placeholder="All categories"
              value={filterCategory}
              onChange={setFilterCategory}
              data={allCategories.map((c) => ({ value: c, label: c }))}
              leftSection={<IconFilter size={14} />}
              clearable
              searchable
              style={{ flex: 1 }}
            />
            <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
              {filtered.length}/{items.length}
            </Text>
          </Group>
        </Stack>
      ) : (
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
            <TextInput
              placeholder="Search items…"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={14} />}
              style={{ maxWidth: 280 }}
            />
            <Select
              placeholder="All categories"
              value={filterCategory}
              onChange={setFilterCategory}
              data={allCategories.map((c) => ({ value: c, label: c }))}
              leftSection={<IconFilter size={14} />}
              clearable
              searchable
              style={{ maxWidth: 220 }}
            />
          </Group>
          <Group gap="xs">
            <Text size="xs" c="dimmed">
              {filtered.length} of {items.length}
            </Text>
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => setCreating(true)}
            >
              Add item
            </Button>
          </Group>
        </Group>
      )}

      {creating && (
        <Group
          gap="xs"
          p="sm"
          style={{
            background: "var(--mantine-color-orange-0)",
            border: "1px solid var(--mantine-color-orange-3)",
            borderRadius: 6,
          }}
        >
          <TextInput
            placeholder="New item name"
            value={newName}
            onChange={(e) => setNewName(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
              if (e.key === "Escape") {
                setCreating(false);
                setNewName("");
              }
            }}
            autoFocus
            style={{ flex: 1 }}
          />
          <Button size="xs" onClick={handleCreate}>
            Create
          </Button>
          <Button
            size="xs"
            variant="subtle"
            onClick={() => {
              setCreating(false);
              setNewName("");
            }}
          >
            Cancel
          </Button>
        </Group>
      )}

      <VirtualizedItems
        items={filtered}
        emptyMessage={
          items.length === 0
            ? "No items on this page yet."
            : "No items match the filter."
        }
        expanded={expanded}
        onToggle={toggle}
        onUpdate={onUpdateItem}
        onDelete={onDeleteItem}
      />
    </Stack>
  );
};

interface VirtualizedItemsProps {
  items: MenuItem[];
  emptyMessage: string;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onUpdate: (itemId: string, updates: Partial<MenuItem>) => Promise<void>;
  onDelete: (itemId: string) => Promise<void>;
}

const VirtualizedItems: React.FC<VirtualizedItemsProps> = ({
  items,
  emptyMessage,
  expanded,
  onToggle,
  onUpdate,
  onDelete,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);

  // Expanded rows are much taller; estimate based on whether each row is open
  const estimateSize = (index: number) => {
    const it = items[index];
    return expanded.has(it.id) ? 720 : 64;
  };

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize,
    overscan: 6,
    getItemKey: (i) => items[i].id,
  });

  return (
    <Box
      ref={parentRef}
      style={{
        flex: 1,
        overflowY: "auto",
        border: "1px solid var(--mantine-color-gray-3)",
        borderRadius: 6,
        background: "white",
        contain: "strict",
      }}
    >
      {items.length === 0 ? (
        <Box p="xl" ta="center">
          <Text c="dimmed" size="sm">
            {emptyMessage}
          </Text>
        </Box>
      ) : (
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: "100%",
            position: "relative",
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const item = items[virtualRow.index];
            return (
              <div
                key={item.id}
                ref={virtualizer.measureElement}
                data-index={virtualRow.index}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <ItemRow
                  item={item}
                  expanded={expanded.has(item.id)}
                  onToggle={() => onToggle(item.id)}
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                />
              </div>
            );
          })}
        </div>
      )}
    </Box>
  );
};
